import {Component, EventEmitter, Input, Output, signal} from "@angular/core";
import {CommonModule} from "@angular/common";
import {BootstrapIcon} from "src/app/services/header.service";

@Component({
    selector: "app-paginator",
    standalone: true,
    imports: [CommonModule],
    template: `
        <nav class="pagination-container" aria-label="Page navigation">
            <ul class="pagination-list">
                <!-- Previous button -->
                <li>
                    <button
                            class="pagination-button"
                            [class.disabled]="activePage() === 1"
                            [disabled]="activePage() === 1"
                            (click)="onPageChange(activePage() - 1)"
                            aria-label="Previous page"
                    >
                        <i [class]="BootstrapIcon.chevron_left"></i>
                    </button>
                </li>

                <!-- First page -->
                <li *ngIf="startPage > 1">
                    <button
                            class="pagination-button"
                            [class.active]="1 === activePage()"
                            (click)="onPageChange(1)"
                    >
                        1
                    </button>
                </li>

                <!-- Left ellipsis -->
                <li *ngIf="startPage > 2">
                    <button
                            class="pagination-button ellipsis"
                            (click)="onPageChange(startPage - 1)"
                    >
                        &hellip;
                    </button>
                </li>

                <!-- Page numbers -->
                <li *ngFor="let page of visiblePages">
                    <button
                            class="pagination-button"
                            [class.active]="page === activePage()"
                            (click)="onPageChange(page)"
                            [attr.aria-current]="page === activePage() ? 'page' : null"
                    >
                        {{ page }}
                    </button>
                </li>

                <!-- Right ellipsis -->
                <li *ngIf="endPage < totalPages - 1">
                    <button
                            class="pagination-button ellipsis"
                            (click)="onPageChange(endPage + 1)"
                    >
                        &hellip;
                    </button>
                </li>

                <!-- Last page -->
                <li *ngIf="endPage < totalPages">
                    <button
                            class="pagination-button"
                            [class.active]="totalPages === activePage()"
                            (click)="onPageChange(totalPages)"
                    >
                        {{ totalPages }}
                    </button>
                </li>

                <!-- Next button -->
                <li>
                    <button
                            class="pagination-button"
                            [class.disabled]="activePage() === totalPages"
                            [disabled]="activePage() === totalPages"
                            (click)="onPageChange(activePage() + 1)"
                            aria-label="Next page"
                    >
                        <i [class]="BootstrapIcon.chevron_right"></i>
                    </button>
                </li>
            </ul>
        </nav>
    `,
    styles: [
        `
          .pagination-container {
            display: flex;
            justify-content: left;
            margin: 20px 0;
          }

          .pagination-list {
            display: flex;
            list-style: none;
            margin: 0;
            padding: 0;
          }

          .pagination-list li {
            margin: 0;
            display: flex;
          }

          .pagination-button {
            padding: 8px 16px;
            border: 1px solid #ddd;
            background-color: white;
            color: #333;
            cursor: pointer;
            min-width: 40px;
            transition: all 0.2s ease;
            margin: 0;
            border-radius: 0;
          }

          /* Remove double borders between buttons */
          .pagination-list li:not(:first-child) .pagination-button {
            border-left: none;
          }

          /* Round corners only on first and last buttons */
          .pagination-list li:first-child .pagination-button {
            border-top-left-radius: 4px;
            border-bottom-left-radius: 4px;
          }

          .pagination-list li:last-child .pagination-button {
            border-top-right-radius: 4px;
            border-bottom-right-radius: 4px;
          }

          .pagination-button:hover:not(.disabled):not(.active) {
            background-color: #f5f5f5;
            border-color: #ccc;
            z-index: 1; /* Ensure hover state border shows above adjacent buttons */
          }

          .pagination-button.active {
            background-color: #000;
            color: white;
            border-color: #000;
            z-index: 2; /* Ensure active state is always on top */
          }

          .pagination-button.disabled {
            background-color: #f5f5f5;
            color: #999;
          }
        `,
    ],
})
export class PaginatorComponent {
    @Input() totalPages: number = 1;
    @Output() pageChange = new EventEmitter<number>();
    BootstrapIcon = BootstrapIcon;
    private maxVisiblePages = 3;

    activePage = signal<number>(1);

    get startPage(): number {
        if (this.totalPages <= this.maxVisiblePages) {
            return 1;
        }

        const halfVisible = Math.floor(this.maxVisiblePages / 2);
        if (this.activePage() <= halfVisible + 1) {
            return 1;
        }

        if (this.activePage() >= this.totalPages - halfVisible) {
            return this.totalPages - this.maxVisiblePages + 1;
        }

        return this.activePage() - halfVisible;
    }

    get endPage(): number {
        if (this.totalPages <= this.maxVisiblePages) {
            return this.totalPages;
        }

        return Math.min(this.startPage + this.maxVisiblePages - 1, this.totalPages);
    }

    get visiblePages(): number[] {
        const pages: number[] = [];
        for (let i = this.startPage; i <= this.endPage; i++) {
            pages.push(i);
        }
        return pages;
    }

    onPageChange(page: number): void {
        if (page >= 1 && page <= this.totalPages) {
            this.setActivePage(page);
            this.pageChange.emit(page);
        }
    }

    setActivePage(page: number) {
        this.activePage.set(page);
    }
}
